import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, ArrowLeft, Image as ImageIcon, MessageCircle, Heart, Send, Trash2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlbumDetay({ user }) {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [liking, setLiking] = useState(false);
  const [likedLocally, setLikedLocally] = useState(false);

  const fetchComments = useCallback(async (albumId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/albums/${albumId}/comments`);
      if (r.ok) setComments(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (slug === 'genel') {
          // Virtual album: photos with no album_id (no comments/likes)
          setAlbum({ name: 'Genel', description: 'Albüm seçilmemiş fotoğraflar', slug: 'genel', id: null });
          const r = await fetch(`${BACKEND_URL}/api/gallery?album_id=none`);
          const data = await r.json();
          setPhotos(Array.isArray(data) ? data : []);
        } else {
          const albumRes = await fetch(`${BACKEND_URL}/api/albums/${slug}`);
          if (!albumRes.ok) throw new Error('not_found');
          const albumData = await albumRes.json();
          setAlbum(albumData);
          const photosRes = await fetch(`${BACKEND_URL}/api/gallery?album_id=${albumData.id}`);
          const data = await photosRes.json();
          setPhotos(Array.isArray(data) ? data : []);
          // Track local "liked" state per device
          const likedSet = JSON.parse(localStorage.getItem('kiziloren_liked_albums') || '[]');
          setLikedLocally(likedSet.includes(albumData.id));
          fetchComments(albumData.id);
        }
      } catch {
        toast.error('Albüm bulunamadı');
        setAlbum(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, fetchComments]);

  const open = (i) => setLightboxIndex(i);
  const close = useCallback(() => setLightboxIndex(-1), []);
  const prev = useCallback(() => setLightboxIndex(i => i > 0 ? i - 1 : photos.length - 1), [photos.length]);
  const next = useCallback(() => setLightboxIndex(i => i < photos.length - 1 ? i + 1 : 0), [photos.length]);

  const handleLike = async () => {
    if (!album?.id || likedLocally || liking) return;
    setLiking(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/albums/${album.id}/like`, { method: 'POST' });
      if (!r.ok) throw new Error('like failed');
      const data = await r.json();
      setAlbum(prev => ({ ...prev, like_count: data.like_count }));
      const likedSet = JSON.parse(localStorage.getItem('kiziloren_liked_albums') || '[]');
      if (!likedSet.includes(album.id)) {
        likedSet.push(album.id);
        localStorage.setItem('kiziloren_liked_albums', JSON.stringify(likedSet));
      }
      setLikedLocally(true);
      toast.success('Beğeninize teşekkürler!');
    } catch {
      toast.error('Beğeni gönderilemedi');
    } finally {
      setLiking(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Yorum yapmak için giriş yapın'); return; }
    if (!newComment.trim() || !album?.id) return;
    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/albums/${album.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment: newComment.trim() }),
      });
      if (!r.ok) throw new Error('failed');
      setNewComment('');
      toast.success('Yorumunuz onay için gönderildi');
    } catch {
      toast.error('Yorum gönderilemedi');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteOwnComment = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/albums/${album.id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('failed');
      toast.success('Yorum silindi');
      fetchComments(album.id);
    } catch {
      toast.error('Yorum silinemedi');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === -1) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, prev, next, close]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <ImageIcon className="w-16 h-16 mx-auto text-amber-300 mb-4" />
          <h2 className="text-xl font-bold text-amber-900 mb-2">Albüm bulunamadı</h2>
          <Link to="/galeri" className="inline-flex items-center gap-1 text-amber-700 hover:text-orange-600 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Foto Galeriye dön
          </Link>
        </div>
      </div>
    );
  }

  const currentPhoto = lightboxIndex >= 0 ? photos[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/60 to-white py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link to="/galeri" className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-orange-600 mb-6 group" data-testid="back-to-albums">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Tüm Albümler
        </Link>

        <div className="mb-8 sm:mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-5xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
              {album.name}
            </h1>
            {album.description && <p className="text-amber-700 text-base sm:text-lg">{album.description}</p>}
            <p className="text-amber-600 text-sm mt-2">{photos.length} fotoğraf</p>
          </div>
          {album.id && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleLike}
                disabled={liking || likedLocally}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                  likedLocally
                    ? 'bg-rose-100 text-rose-600 cursor-default'
                    : 'bg-white border-2 border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 shadow-sm'
                }`}
                data-testid="album-like-btn"
              >
                <Heart className={`w-4 h-4 ${likedLocally ? 'fill-rose-500 text-rose-500' : ''}`} />
                <span>{likedLocally ? 'Beğenildi' : 'Beğen'}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-xs">{album.like_count || 0}</span>
              </button>
            </div>
          )}
        </div>

        {/* Photo grid */}
        {photos.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-amber-200 rounded-3xl p-10 sm:p-16 text-center">
            <ImageIcon className="w-12 h-12 mx-auto text-amber-300 mb-3" />
            <p className="text-amber-700">Bu albümde henüz fotoğraf yok</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => open(i)}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-amber-100 hover:shadow-xl transition-all active:scale-[0.98]"
                data-testid={`photo-${p.id}`}
              >
                <img
                  src={p.image_url}
                  alt={p.title || ''}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  {p.title && <p className="text-white text-xs sm:text-sm font-medium line-clamp-2">{p.title}</p>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Share buttons (only for real albums, not the virtual 'genel') */}
        {album.id && (
          <div className="mt-10 max-w-3xl mx-auto pt-6 border-t border-amber-200">
            <ShareButtons title={album.name} context="Foto Galeri" />
          </div>
        )}

        {/* Comments section (only for real albums) */}
        {album.id && (
          <section className="mt-12 sm:mt-16 max-w-3xl mx-auto" data-testid="album-comments-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
                  Yorumlar
                </h2>
                <p className="text-xs text-amber-600">{comments.length} yorum</p>
              </div>
            </div>

            {/* Comment form */}
            {user ? (
              <form onSubmit={handleSubmitComment} className="bg-white rounded-2xl border border-amber-100 p-4 sm:p-5 mb-6 shadow-sm" data-testid="album-comment-form">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {user.nickname?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <Textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder="Albüm hakkında düşüncelerinizi paylaşın..."
                      rows={3}
                      maxLength={500}
                      className="border-amber-200 focus:border-amber-400 resize-none"
                      data-testid="album-comment-input"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-amber-600">Yorumunuz onaylandıktan sonra yayınlanır</span>
                      <Button type="submit" disabled={submittingComment || !newComment.trim()} className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full" data-testid="album-comment-submit">
                        <Send className="w-4 h-4 mr-1.5" />Gönder
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 text-center">
                <p className="text-sm text-amber-800 mb-3">Yorum yapabilmek için üye girişi yapmalısınız</p>
                <Link to="/uyelik">
                  <Button className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full">
                    Üye Girişi
                  </Button>
                </Link>
              </div>
            )}

            {/* Comments list */}
            {comments.length === 0 ? (
              <div className="text-center py-8 text-amber-600 text-sm">
                Henüz onaylanmış yorum yok. İlk yorumu siz yapın!
              </div>
            ) : (
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl border border-amber-100 p-4 sm:p-5 shadow-sm" data-testid={`album-comment-${c.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 flex items-center justify-center text-amber-700 font-bold flex-shrink-0">
                        {c.user_nickname?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-amber-900">{c.user_nickname}</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                              <Clock className="w-3 h-3" />
                              {new Date(c.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          {user && (c.user_id === user.id || user.role === 'admin') && (
                            <button
                              type="button"
                              onClick={() => handleDeleteOwnComment(c.id)}
                              className="text-red-400 hover:text-red-600 transition-colors p-1"
                              data-testid={`delete-album-comment-${c.id}`}
                              aria-label="Yorumu sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-amber-900/90 leading-relaxed whitespace-pre-wrap break-words">{c.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
          data-testid="lightbox"
          onClick={close}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-3 sm:top-5 right-3 sm:right-5 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-md transition-colors"
            aria-label="Kapat"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-3 sm:top-5 left-3 sm:left-5 z-10 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md text-white text-xs sm:text-sm font-medium">
            {lightboxIndex + 1} / {photos.length}
          </div>

          {/* Previous */}
          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-md transition-colors active:scale-95"
              aria-label="Önceki"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>
          )}

          {/* Next */}
          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-md transition-colors active:scale-95"
              aria-label="Sonraki"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>
          )}

          {/* Image */}
          <div className="relative max-w-7xl w-full h-full flex items-center justify-center p-4 sm:p-12" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentPhoto.image_url}
              alt={currentPhoto.title || ''}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              key={currentPhoto.id}
            />
          </div>

          {/* Caption */}
          {(album?.name || currentPhoto.description) && (
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-center bg-gradient-to-t from-black/80 to-transparent">
              {album?.name && (
                <h3 className="text-white text-base sm:text-lg font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif' }}>
                  {album.name}
                </h3>
              )}
              {currentPhoto.description && (
                <p className="text-amber-100/80 text-sm">{currentPhoto.description}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
